/**
 * Shared kit for the charm builders (Build Your Floating Locket, Build Your Mini Charm Bracelet).
 *
 * Everything here is builder-agnostic and was factored out of floating-locket-builder.js so both builders run the same
 * code: the polite status region and native <dialog> handling, the photo dialog (Pintura crop -> Cloudinary upload),
 * the variant picker (letters, numbers, birthstones), one-at-a-time add to cart + the cart drawer, image loading,
 * canvas sizing, the set rail's arrows, the phone Add-to-cart placement and the sticky header offset.
 * The markup hooks are the shared `data-fl-*` attributes and `fl-*` classes (assets/floating-locket-builder.css is the
 * shared stylesheet); each builder adds its own state, preview and cart properties.
 *
 * Accessibility (CLAUDE.md, WCAG 2.2 AA): native buttons, native <dialog> (focus moves in, Escape and the backdrop
 * close, focus returns to the opener), one polite status region per page / dialog.
 */
(function () {
  'use strict';
  if (window.WFBuilderKit) return;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------------------------------- utils */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(cents) {
    var s = (cents / 100).toFixed(2);
    return '$' + s.replace(/\.00$/, '');
  }
  function uid() { return 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }
  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }
  function track(event, data) {
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push(Object.assign({ event: event }, data || {})); } catch (e) { /* ok */ }
  }

  var IMG = {};
  function loadImg(url) {
    if (!url) return Promise.reject(new Error('no url'));
    if (IMG[url]) return IMG[url];
    IMG[url] = new Promise(function (res, rej) {
      var im = new Image();
      im.decoding = 'async';
      im.onload = function () { res(im); };
      im.onerror = function () { delete IMG[url]; rej(new Error('image ' + url)); };
      im.src = url;
    });
    return IMG[url];
  }
  /** A scratch canvas whose 2D context smooths at HIGH quality: the previews shrink 32 px/mm charm art to 5-17 px/mm,
   *  and the default "low" (bilinear) filter left them soft and crunchy (owner 10/15: "they seem a little blurry"). */
  function makeCanvas(w, h) {
    var c = document.createElement('canvas'); c.width = Math.max(1, w); c.height = Math.max(1, h);
    smooth(c.getContext('2d'));
    return c;
  }
  function smooth(x) { if (x) { x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high'; } return x; }
  /** Size a square canvas's backing store to its CSS box x devicePixelRatio (capped). */
  function fitCanvas(cv) {
    if (!cv) return;
    var r = cv.getBoundingClientRect();
    // full device resolution (iPhones are 3x), times the pinch zoom (visualViewport.scale), which magnifies the canvas
    // too (owner 10/15, a pinch-zoomed phone: "still looks blurry"); capped so a phone's canvas memory stays sane
    var vv = window.visualViewport, zoom = vv && vv.scale > 1 ? Math.min(vv.scale, 4) : 1;
    var dpr = Math.min(window.devicePixelRatio || 1, 3) * zoom;
    var px = Math.max(48, Math.min(2048, Math.round(r.width * dpr)));
    if (cv.width !== px) { cv.width = px; cv.height = px; }
  }

  /* ------------------------------------------------------------------------ status region + dialogs */
  /**
   * ui(root, {dialogs: {key: <dialog>}}) -> {announce, openDialog, closeDialog, dialogs}
   * While a dialog is modal the page (and its status region) is inert, so announcements go to the topmost dialog's
   * own status region. Dialog cleanup runs synchronously in closeDialog / Escape, not on the queued 'close' event: a
   * browser that isn't painting may never deliver it, which once left the page scroll-locked.
   */
  function ui(root, opts) {
    opts = opts || {};
    var dialogs = opts.dialogs || {};
    var statusEl = root.querySelector('[data-fl-status]');
    var stack = [];
    var timer = null;
    var openers = new WeakMap();

    function announce(msg) {
      var top = stack[stack.length - 1];
      var el = (top && top.querySelector('[data-fl-dlg-status]')) || statusEl;
      if (!el) return;
      [statusEl].concat(stack.map(function (d) { return d.querySelector('[data-fl-dlg-status]'); }))
        .forEach(function (x) { if (x) x.textContent = ''; });
      clearTimeout(timer);
      timer = setTimeout(function () { el.textContent = msg; }, 60);
    }
    function openDialog(d, opener) {
      if (!d) return;
      d.__flClosed = false;
      // move to <body> so no ancestor's transform/overflow clips it (and it is outside the product form)
      if (d.parentNode !== document.body) document.body.appendChild(d);
      openers.set(d, opener || document.activeElement);
      document.documentElement.classList.add('fl-modal-open');
      if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
      stack = stack.filter(function (x) { return x !== d; }).concat([d]);
      var first = d.querySelector('[data-fl-autofocus]') || d.querySelector('.fl-dialog__close');
      if (first) first.focus();
    }
    function closeDialog(d) {
      if (!d) return;
      if (d.open) { if (typeof d.close === 'function') d.close(); else d.removeAttribute('open'); }
      afterClose(d);
    }
    function afterClose(d) {
      if (d.__flClosed) return;
      d.__flClosed = true;
      stack = stack.filter(function (x) { return x !== d; });
      var ds = d.querySelector('[data-fl-dlg-status]');
      if (ds) ds.textContent = '';
      if (d.__flCleanup) d.__flCleanup();
      if (!Object.keys(dialogs).some(function (x) { return dialogs[x] && dialogs[x].open; })) {
        document.documentElement.classList.remove('fl-modal-open');
      }
      var o = openers.get(d);
      if (o && document.body.contains(o) && o.focus) o.focus();
      if (d.__onClose) { var f = d.__onClose; d.__onClose = null; f(); }
    }
    Object.keys(dialogs).forEach(function (k) {
      var d = dialogs[k];
      if (!d) return;
      d.addEventListener('cancel', function () { closeDialog(d); });   // Escape (fires synchronously)
      d.addEventListener('close', function () { if (!d.open) afterClose(d); }); // any other path
      d.addEventListener('click', function (e) {
        if (e.target === d) closeDialog(d); // backdrop
        if (e.target.closest && e.target.closest('[data-fl-close]')) closeDialog(d);
      });
    });
    return { announce: announce, openDialog: openDialog, closeDialog: closeDialog, dialogs: dialogs };
  }

  /* ------------------------------------------------------------- photo: choose -> crop -> Cloudinary */
  function photoFromUrl(url) {
    var thumb = url.replace('/upload/', '/upload/c_fill,g_auto,w_400,h_400/');
    return { url: url, thumb: thumb };
  }
  /**
   * photoDialog(<dialog>, ui, {cloudinary: {url, preset}, log}) -> {open(ctx, opener)}
   * ctx: {onDone(photo, name), aspect (w/h, default 1), shape 'circle'|'rect' (the crop guide), name: {label, max,
   * value, help} to show the optional name field (the mini portrait charm), title}
   */
  function photoDialog(d, U, opts) {
    if (!d) return { open: function () {} };
    var LOG = opts.log || '[Builder]';
    var ctx = null, seq = 0, xhr = null, editor = null, picked = null;
    var q = function (s) { return d.querySelector(s); };
    var titleEl = q('[data-fl-photo-title]');
    var defaultTitle = titleEl ? titleEl.textContent : '';
    var nameWrap = q('[data-fl-photo-name-wrap]'), nameInput = q('[data-fl-photo-name]');

    function cancelUpload() {
      seq++;                                     // any upload still running belongs to a closed session now
      if (xhr) { try { xhr.abort(); } catch (e) { /* ok */ } xhr = null; }
    }
    function reset() {
      if (editor) { try { editor.destroy(); } catch (e) { /* ok */ } editor = null; }
      picked = null;
      var ed = q('[data-fl-photo-editor]');
      ed.hidden = true; ed.innerHTML = '';
      q('[data-fl-photo-drop]').hidden = false;
      q('[data-fl-photo-use]').disabled = true;
      q('[data-fl-photo-use]').textContent = 'Use this photo';
      q('[data-fl-photo-status]').textContent = '';
      q('[data-fl-photo-error]').textContent = '';
      q('[data-fl-photo-input]').value = '';
    }
    function open(c, opener) {
      cancelUpload();
      ctx = c || {};
      reset();
      if (titleEl) titleEl.textContent = ctx.title || defaultTitle;
      if (nameWrap && nameInput) {
        nameWrap.hidden = !ctx.name;
        if (ctx.name) {
          nameInput.value = ctx.name.value || '';
          nameInput.maxLength = ctx.name.max || 12;
          var lab = nameWrap.querySelector('label'); if (lab) lab.textContent = ctx.name.label || 'Name (optional)';
          var hp = nameWrap.querySelector('[data-fl-photo-name-help]'); if (hp) hp.textContent = ctx.name.help || '';
        }
      }
      // an existing photo (Change photo): the name can be edited without choosing the photo again
      if (ctx.current) q('[data-fl-photo-use]').disabled = false;
      U.openDialog(d, opener);
    }
    var inp = q('[data-fl-photo-input]');
    inp.addEventListener('change', function () {
      var f = inp.files && inp.files[0];
      if (!f) return;
      if (!/^image\//.test(f.type) && !/\.(heic|heif)$/i.test(f.name)) {
        q('[data-fl-photo-error]').textContent = 'Please choose an image file.';
        return;
      }
      picked = f;
      q('[data-fl-photo-error]').textContent = '';
      showEditor(f);
      q('[data-fl-photo-use]').focus();   // the file input's label is hidden now: don't leave focus on it
    });
    q('[data-fl-photo-use]').addEventListener('click', use);
    d.__flCleanup = function () {
      cancelUpload();
      if (editor) { try { editor.destroy(); } catch (e) { /* ok */ } editor = null; }
    };

    function showEditor(file) {
      var ed = q('[data-fl-photo-editor]'), drop = q('[data-fl-photo-drop]'), btn = q('[data-fl-photo-use]');
      var status = q('[data-fl-photo-status]');
      var aspect = ctx.aspect || 1, round = (ctx.shape || 'circle') === 'circle';
      drop.hidden = true;
      ed.hidden = false;
      btn.disabled = false;
      if (window.pintura && window.pintura.appendDefaultEditor) {
        ed.innerHTML = '<div class="fl-pintura" data-fl-pintura></div>';
        try {
          editor = window.pintura.appendDefaultEditor(ed.firstChild, {
            src: file,
            utils: ['crop'],
            enableButtonExport: false,
            imageCropAspectRatio: aspect,
            cropEnableButtonFlipHorizontal: false,
            cropEnableButtonFlipVertical: false,
            imageCropLimitToImage: true,
            imageReader: window.pintura.createDefaultImageReader(),
            imageWriter: window.pintura.createDefaultImageWriter({ mimeType: 'image/jpeg', quality: 0.9, targetSize: { width: 1400, height: 1400, fit: 'contain', upscale: false } }),
            willRenderCanvas: function (shapes, st) {
              if (!round || !st.utilVisibility || st.utilVisibility.crop <= 0) return shapes;
              var sel = st.selectionRect;
              return Object.assign({}, shapes, {
                interfaceShapes: [{
                  x: sel.x + sel.width * 0.5, y: sel.y + sel.height * 0.5, rx: sel.width * 0.5, ry: sel.height * 0.5,
                  opacity: st.utilVisibility.crop, inverted: true,
                  backgroundColor: [].concat(st.backgroundColor, [0.55]), strokeWidth: 1, strokeColor: st.lineColor
                }].concat(shapes.interfaceShapes)
              });
            }
          });
          status.textContent = round ? 'Drag and zoom to center the photo in the circle.' : 'Drag and zoom to frame the photo.';
          return;
        } catch (e) {
          console.warn(LOG + ' Pintura failed, using a centered crop', e);
          editor = null;
        }
      }
      // no editor: show the centered crop that will be used
      var url = URL.createObjectURL(file);
      ed.innerHTML = '<div class="fl-photo__fallback' + (round ? '' : ' is-rect') + '"><img src="' + url + '" alt="Your photo, centered" style="aspect-ratio:' + aspect + '"></div>';
      status.textContent = 'Your photo will be centered in the charm.';
    }
    function centeredCrop(file, aspect) {
      return new Promise(function (res, rej) {
        var url = URL.createObjectURL(file);
        var im = new Image();
        im.onload = function () {
          var iw = im.naturalWidth, ih = im.naturalHeight;
          var cw = Math.min(iw, ih * aspect), ch = cw / aspect;
          var k = Math.min(1, 1400 / Math.max(cw, ch));
          var c = makeCanvas(Math.round(cw * k), Math.round(ch * k));
          c.getContext('2d').drawImage(im, (iw - cw) / 2, (ih - ch) / 2, cw, ch, 0, 0, c.width, c.height);
          c.toBlob(function (b) { URL.revokeObjectURL(url); b ? res(b) : rej(new Error('crop')); }, 'image/jpeg', 0.9);
        };
        im.onerror = function () { rej(new Error('Could not read that image.')); };
        im.src = url;
      });
    }
    function upload(blob, onProgress) {
      return new Promise(function (res, rej) {
        var fd = new FormData();
        fd.append('file', blob, 'photo.jpg');
        fd.append('upload_preset', opts.cloudinary.preset);
        var x = xhr = new XMLHttpRequest();
        x.open('POST', opts.cloudinary.url);
        x.upload.onprogress = function (e) { if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total); };
        x.onload = function () {
          try {
            var j = JSON.parse(x.responseText);
            if (x.status >= 200 && x.status < 300 && (j.secure_url || j.url)) return res(j.secure_url || j.url);
          } catch (e) { /* fallthrough */ }
          rej(new Error('Upload failed'));
        };
        x.onerror = function () { rej(new Error('Upload failed')); };
        x.onabort = function () { rej(new Error('cancelled')); };
        x.send(fd);
      });
    }
    function nameValue() {
      return nameWrap && !nameWrap.hidden && nameInput ? nameInput.value.replace(/\s+/g, ' ').trim() : null;
    }
    function use() {
      var btn = q('[data-fl-photo-use]'), status = q('[data-fl-photo-status]'), err = q('[data-fl-photo-error]');
      var c = ctx, tok = seq;
      if (!picked) {                                   // name edited on an existing photo
        if (c && c.current) { U.closeDialog(d); if (c.onDone) c.onDone(c.current, nameValue()); }
        return;
      }
      function current() { return tok === seq && d.open; }
      btn.disabled = true;
      btn.textContent = 'Uploading…';
      err.textContent = '';
      var getBlob = editor ? editor.processImage().then(function (r) { return r.dest; }) : centeredCrop(picked, c.aspect || 1);
      var localUrl = null;
      getBlob.then(function (blob) {
        if (!current()) throw new Error('cancelled');
        localUrl = URL.createObjectURL(blob);
        return upload(blob, function (p) { if (current()) status.textContent = 'Uploading ' + Math.round(p * 100) + '%'; });
      }).then(function (url) {
        xhr = null;
        if (!current()) return;            // the shopper closed or reopened the dialog: drop this result
        var photo = photoFromUrl(url);
        photo.local = localUrl;           // draw the local copy right away; the Cloudinary URL is what we save
        var name = nameValue();
        status.textContent = 'Photo added.';
        U.closeDialog(d);
        if (c && c.onDone) c.onDone(photo, name);
      }).catch(function (e) {
        if (!current()) return;
        console.error(LOG + ' photo', e);
        btn.disabled = false;
        btn.textContent = 'Use this photo';
        status.textContent = '';
        err.textContent = 'We couldn’t upload that photo. Please check your connection and try again.';
      });
    }
    return { open: open };
  }

  /* --------------------------------------------------------------------------------- catalog helpers */
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var BS_MONTH = { Garnet: 0, Amethyst: 1, Aquamarine: 2, Diamond: 3, Emerald: 4, Pearl: 5, Ruby: 6, Peridot: 7, Sapphire: 8, Opal: 9, Topaz: 10, Turquoise: 11 };
  /** p.short ("Letter"), p.pickOption (the option a shopper picks: Letter / Number / Birthstone; never Metal), p.multi */
  function prepProduct(p, shortName) {
    p.short = shortName(p.title);
    var opt = (p.options || []).filter(function (o) { return !/^(title|metal|color|colour)$/i.test(o); })[0] || null;
    p.pickOption = opt;
    p.multi = p.variants.length > 1 && !!opt;
    p.variants.forEach(function (v) { v.product = p; });
    return p;
  }
  function variantValue(v) {
    var p = v.product;
    var idx = p.pickOption ? p.options.indexOf(p.pickOption) : -1;
    return idx > -1 ? v.options[idx] : v.title;
  }
  function isStone(p) { return /birthstone/i.test(p.pickOption || ''); }
  /** "Letter E", "Birthstone · September", "Mama" */
  function itemName(v) {
    var p = v.product;
    if (!p.multi) return p.short;
    var val = variantValue(v);
    if (isStone(p) && BS_MONTH.hasOwnProperty(val)) return 'Birthstone · ' + MONTHS[BS_MONTH[val]];
    return p.short + ' ' + val;
  }
  function variantLabel(v) {
    var val = variantValue(v);
    if (isStone(v.product) && BS_MONTH.hasOwnProperty(val)) return MONTHS[BS_MONTH[val]] + ' · ' + val;
    return val;
  }
  function rangeLabel(p, variants) {
    var vals = (variants || p.variants).map(variantValue);
    if (isStone(p)) return 'All 12 months';
    return vals[0] + '–' + vals[vals.length - 1];
  }

  /* ---------------------------------------------------------------------------- variant picker dialog */
  /**
   * picker(<dialog>, ui, esc) -> {open(p, opener, cfg), openOne(p, opener, cfg)}
   * open: tap to add, "-" to remove one (cfg: variants, count(v), add(v) -> bool, removeOne(v), help(), thumb(v)).
   * openOne: a single choice that closes the dialog (a set's birth month): cfg: variants, current, thumb(v), title,
   * onPick(v) (runs after the dialog closed and focus returned).
   */
  function picker(d, U) {
    if (!d) return { open: function () {}, openOne: function () {} };
    var q = function (s) { return d.querySelector(s); };
    function open(p, opener, cfg) {
      var vs = cfg.variants || p.variants;
      q('[data-fl-picker-title]').textContent = cfg.title || ('Choose ' + (isStone(p) ? 'birth months' : p.short.toLowerCase() + 's'));
      var list = q('[data-fl-picker-list]');
      list.setAttribute('aria-label', p.short + ' options');
      list.className = 'fl-picker' + (isStone(p) ? ' fl-picker--list' : ' fl-picker--grid');
      function draw() {
        q('[data-fl-picker-help]').textContent = cfg.help ? cfg.help() : '';
        list.innerHTML = vs.map(function (v) {
          var c = cfg.count(v);
          var lab = variantLabel(v);
          var dis = !v.available;
          var thumb = isStone(p) && cfg.thumb ? '<img src="' + esc(cfg.thumb(v)) + '" alt="" width="40" height="40" loading="lazy">' : '';
          return '<span class="fl-pick-wrap">' +
            '<button type="button" class="fl-pick' + (c ? ' is-in' : '') + '" data-v="' + v.id + '"' + (dis ? ' aria-disabled="true" aria-describedby="FlPickerHelp"' : '') + '>' +
              thumb + '<span class="fl-pick__label">' + esc(lab) + '</span>' +
              (c ? '<span class="fl-pick__count" aria-hidden="true">×' + c + '</span>' : '') +
              '<span class="visually-hidden">' + (c ? ', ' + c + ' added' : '') + (!v.available ? ', sold out' : '') + '</span>' +
            '</button>' +
            (c ? '<button type="button" class="fl-pick__minus" data-minus="' + v.id + '"><svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="12" height="12"><path d="M5 12h14" stroke="currentColor" stroke-width="2.6"/></svg><span class="visually-hidden">Remove one ' + esc(lab) + '</span></button>' : '') +
          '</span>';
        }).join('');
      }
      draw();
      list.onclick = function (e) {
        var minus = e.target.closest('[data-minus]');
        if (minus) {
          var mv = vs.filter(function (x) { return x.id === Number(minus.getAttribute('data-minus')); })[0];
          if (mv) cfg.removeOne(mv);
          draw();
          var again = list.querySelector('[data-v="' + (mv && mv.id) + '"]');
          if (again) again.focus();
          return;
        }
        var b = e.target.closest('[data-v]');
        if (!b || b.getAttribute('aria-disabled') === 'true') return;   // sold out
        var v = vs.filter(function (x) { return x.id === Number(b.getAttribute('data-v')); })[0];
        if (v && v.available) {
          cfg.add(v);
          draw();
          var same = list.querySelector('[data-v="' + v.id + '"]');
          if (same) same.focus();
        }
      };
      U.openDialog(d, opener);
    }
    function openOne(p, opener, cfg) {
      var vs = cfg.variants || p.variants;
      q('[data-fl-picker-title]').textContent = cfg.title || 'Choose a birth month';
      q('[data-fl-picker-help]').textContent = '';
      var list = q('[data-fl-picker-list]');
      list.setAttribute('aria-label', cfg.listLabel || 'Birth months');
      list.className = 'fl-picker fl-picker--stones';
      list.innerHTML = vs.map(function (v) {
        var val = variantValue(v), m = BS_MONTH.hasOwnProperty(val) ? MONTHS[BS_MONTH[val]] : val;
        return '<button type="button" class="fl-stone" data-v="' + v.id + '" aria-pressed="' + (v === cfg.current) + '"' + (v.available ? '' : ' disabled') + '>' +
          '<img src="' + esc(cfg.thumb(v)) + '" alt="" width="44" height="44">' +
          '<span class="fl-stone__month">' + esc(m) + '</span>' +
          '<span class="fl-stone__name">' + esc(val) + (v.available ? '' : ' · sold out') + '</span></button>';
      }).join('');
      list.onclick = function (e) {
        var b = e.target.closest('[data-v]');
        if (!b || b.disabled) return;
        var v = vs.filter(function (x) { return x.id === Number(b.getAttribute('data-v')); })[0];
        if (!v) return;
        d.__onClose = function () { cfg.onPick(v); };
        U.closeDialog(d);
      };
      U.openDialog(d, opener);
      var cur = list.querySelector('[aria-pressed="true"]');
      if (cur) cur.focus();
    }
    return { open: open, openOne: openOne };
  }

  /* -------------------------------------------------------------------------------------------- cart */
  var adding = false;
  /**
   * addLines(lines, {btn, label, log, soldOut, failed, onAdded()}) -> Promise<{ok, msg, busy}>
   * One request at a time: a double click or a held Enter must not add two bundles.
   */
  function addLines(lines, o) {
    o = o || {};
    if (adding) return Promise.resolve({ ok: false, busy: true });
    adding = true;
    var btn = o.btn, label = btn ? btn.textContent : '';
    if (btn) { btn.setAttribute('aria-busy', 'true'); btn.classList.add('is-adding'); btn.textContent = 'Adding…'; }
    return fetch('/cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: lines })
    }).then(function (r) {
      return r.json().then(function (j) { if (!r.ok) throw new Error(j && (j.description || j.message) || 'cart'); return j; });
    }).then(function () {
      adding = false;
      if (btn) {
        btn.removeAttribute('aria-busy'); btn.classList.remove('is-adding'); btn.textContent = 'Added to cart';
        setTimeout(function () { btn.textContent = label; if (o.onRestore) o.onRestore(); }, 2200);
      }
      openCart();
      if (o.onAdded) o.onAdded();
      return { ok: true };
    }).catch(function (e) {
      adding = false;
      console.error((o.log || '[Builder]') + ' cart', e);
      if (btn) { btn.removeAttribute('aria-busy'); btn.classList.remove('is-adding'); btn.textContent = label; }
      var msg = /sold out|not available|inventory/i.test(e.message) ? o.soldOut : o.failed;
      return { ok: false, msg: msg || 'Something went wrong. Please try again.' };
    });
  }
  function openCart() {
    var jq = window.jQuery;
    if (window.ShopifyAPI && typeof window.ShopifyAPI.getCart === 'function' && jq) {
      window.ShopifyAPI.getCart(function (cart) {
        jq('body').trigger('afterAddToCart.ajaxCart', cart);
        var link = document.querySelector('.cart-link, .js-drawer-open-cart, [data-cart-link]');
        if (link) link.click();
      });
    } else if (jq) {
      jq(document.body).trigger('added.ajaxProduct');
    } else {
      window.location.href = '/cart';
    }
  }

  /* -------------------------------------------------------------------------------------- navigation */
  function goTo(id) {
    var el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
    setTimeout(function () { try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } }, REDUCED ? 0 : 450);
  }
  /** The theme's sticky header height -> CSS var --fl-top, so the strip/dock sit just under it. Returns sync(). */
  function stickyOffset() {
    function headerOffset() {
      // the sticky element is #shopify-section-header (position: sticky) on this theme; .site-header inside it is not
      var best = 0;
      ['shopify-section-header', null].forEach(function (id) {
        var hs = id ? [document.getElementById(id)] : Array.prototype.slice.call(document.querySelectorAll('.site-header, header'));
        hs.forEach(function (h) {
          if (!h) return;
          var pos = getComputedStyle(h).position;
          if (pos !== 'fixed' && pos !== 'sticky') return;
          var r = h.getBoundingClientRect();
          if (r.top <= 1 && r.bottom > best) best = Math.round(r.bottom);
        });
      });
      return best;
    }
    var tick = false;
    function sync() {
      if (tick) return;
      tick = true;
      requestAnimationFrame(function () {
        tick = false;
        document.documentElement.style.setProperty('--fl-top', headerOffset() + 'px');
      });
    }
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    return sync;
  }
  /** The set rail's arrows (hidden when everything fits). */
  function railNav(rail, nav) {
    if (!nav || !rail) return;
    function update() {
      var over = rail.scrollWidth > rail.clientWidth + 4;
      nav.hidden = !over;
      var prev = nav.querySelector('[data-fl-rail="-1"]'), next = nav.querySelector('[data-fl-rail="1"]');
      if (prev) prev.setAttribute('aria-disabled', String(rail.scrollLeft < 4));
      if (next) next.setAttribute('aria-disabled', String(rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4));
    }
    Array.prototype.forEach.call(nav.querySelectorAll('[data-fl-rail]'), function (b) {
      b.addEventListener('click', function () {
        if (b.getAttribute('aria-disabled') === 'true') return;
        rail.scrollBy({ left: Number(b.getAttribute('data-fl-rail')) * rail.clientWidth * 0.8, behavior: REDUCED ? 'auto' : 'smooth' });
      });
    });
    rail.addEventListener('scroll', function () { requestAnimationFrame(update); }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }
  /**
   * Add to cart placement (owner 2026-10-14): on phones the review's actions block sits right under the first line
   * (above the charm list), so it stays on screen after "Review" however many charms there are; wider screens keep it
   * at the end. The block is MOVED (not reordered with CSS) so the tab order matches what's on screen.
   */
  function placeActions(box, before) {
    var mq = window.matchMedia && window.matchMedia('(max-width: 768px)');
    if (!box || !mq || !before) return;
    var body = box.parentNode, cur = null;
    function place() {
      var top = mq.matches;
      if (top === cur) return;                    // only move on a real breakpoint change (keeps focus, no reflow)
      cur = top;
      var had = box.contains(document.activeElement) ? document.activeElement : null;
      if (top) body.insertBefore(box, before); else body.appendChild(box);
      box.classList.toggle('is-top', top);
      if (had && document.activeElement !== had) had.focus({ preventScroll: true });
    }
    place();
    if (mq.addEventListener) mq.addEventListener('change', place); else if (mq.addListener) mq.addListener(place);
    window.addEventListener('resize', place);     // belt and braces: some webviews are late with the change event
  }

  /* --------------------------------------------------------------------------------- photo upload control */
  var ICON_UPLOAD = '<svg viewBox="0 0 24 24" width="24" height="24" focusable="false"><path d="M12 15V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_CHECK = '<svg class="fl-upload__check" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  /**
   * The quick-start set dialog's photo upload (owner 2026-10-15: "improve the UI of the upload your photo button"):
   * one full-width tap target, dashed while empty (upload icon, "Upload your photo", a hint, a "Choose" pill), solid
   * with the photo, a check + "Photo added" and a "Change" pill once there is one. Styles: .fl-upload in
   * floating-locket-builder.css. o = { hook: 'data-x="i"' (the click hook), labelId (described by), photo: the
   * thumbnail URL or null, rect: true when the charm's window is a tall rectangle (the portrait) }.
   */
  function uploadButton(o) {
    return '<button type="button" class="fl-upload' + (o.photo ? ' has-photo' : '') + '" ' + o.hook + ' aria-describedby="' + esc(o.labelId) + '">' +
      '<span class="fl-upload__media' + (o.rect ? ' fl-upload__media--rect' : '') + '" aria-hidden="true">' +
        (o.photo ? '<img src="' + esc(o.photo) + '" alt="" width="56" height="56">' : ICON_UPLOAD) + '</span>' +
      '<span class="fl-upload__text">' +
        '<span class="fl-upload__title">' + (o.photo ? ICON_CHECK + 'Photo added' : 'Upload your photo') + '</span>' +
        '<span class="fl-upload__hint">' + (o.photo ? 'Change it or adjust the crop' : 'A clear, close-up photo prints best.') + '</span>' +
      '</span>' +
      '<span class="fl-upload__go" aria-hidden="true">' + (o.photo ? 'Change' : 'Choose') + '</span>' +
    '</button>';
  }

  /* ------------------------------------------------------------------------------ scrollable pill rows */
  /**
   * A horizontally scrolling pill row only reads as scrollable when the pill at its right edge is visibly cut
   * (owner 2026-10-15: "always show at least 25% of it so the user always knows its scrollable"). Picks the row's gap
   * (o.min-o.max px, default 6-16) and, when the gap alone can't do it, every pill's side padding (CSS padding -4 to
   * +6 px) so that pill shows 25-75% of itself (half, ideally), staying as close to the CSS as it can. A row that fits
   * does nothing. Re-fits on resize and once the web fonts load. Returns the fit function.
   */
  function pillPeek(row, o) {
    if (!row) return function () {};
    o = o || {};
    var min = o.min || 6, max = o.max || 16;
    function fit() {
      var all = Array.prototype.slice.call(row.children);
      row.style.columnGap = '';
      all.forEach(function (el) { el.style.paddingLeft = el.style.paddingRight = ''; });
      var pills = all.filter(function (el) { return el.offsetParent !== null; });
      if (pills.length < 2 || !row.clientWidth) return;
      var cs = getComputedStyle(row);
      var base = parseFloat(cs.columnGap) || 8;
      var pad = parseFloat(getComputedStyle(pills[0]).paddingLeft) || 0;
      var w = pills.map(function (el) { return el.getBoundingClientRect().width; });
      var visible = row.clientWidth - (parseFloat(cs.paddingLeft) || 0);
      var total = w.reduce(function (a, b) { return a + b; }, 0) + base * (w.length - 1);
      if (total <= visible + 1) return;                       // everything fits: nothing to hint at
      var best = null, bestScore = Infinity;
      for (var e = Math.max(-4, -pad); e <= 6; e++) {         // e = px added to each side of every pill
        var before = 0;
        for (var k = 1; k < w.length; k++) {                  // pill k is the one the right edge cuts
          before += w[k - 1] + 2 * e;
          var wk = w[k] + 2 * e;
          for (var t = 0.25; t <= 0.751; t += 0.05) {         // t = the share of pill k left showing
            var g = (visible - t * wk - before) / k;
            if (g < min || g > max) continue;
            var score = Math.abs(t - 0.5) * 40 + Math.abs(g - base) * 1.5 + Math.abs(e) * 3;
            if (score < bestScore) { bestScore = score; best = { g: g, e: e }; }
          }
        }
      }
      if (!best) return;
      row.style.columnGap = best.g.toFixed(1) + 'px';
      if (best.e) pills.forEach(function (el) { el.style.paddingLeft = el.style.paddingRight = (pad + best.e) + 'px'; });
    }
    if (!row._pillPeek) {
      row._pillPeek = true;
      var timer = 0;                                         // a timer, not rAF: a background tab never runs rAF
      window.addEventListener('resize', function () { clearTimeout(timer); timer = setTimeout(fit, 120); });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
    }
    fit();
    return fit;
  }

  window.WFBuilderKit = {
    REDUCED: REDUCED, esc: esc, money: money, uid: uid, plural: plural, track: track,
    loadImg: loadImg, makeCanvas: makeCanvas, smooth: smooth, fitCanvas: fitCanvas,
    ui: ui, photoDialog: photoDialog, photoFromUrl: photoFromUrl, picker: picker,
    MONTHS: MONTHS, BS_MONTH: BS_MONTH, prepProduct: prepProduct, variantValue: variantValue, itemName: itemName,
    variantLabel: variantLabel, isStone: isStone, rangeLabel: rangeLabel,
    addLines: addLines, openCart: openCart, goTo: goTo, stickyOffset: stickyOffset, railNav: railNav,
    placeActions: placeActions, uploadButton: uploadButton, pillPeek: pillPeek
  };
})();
